import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import './HalamanEditor.css';

const HalamanEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showCode, setShowCode] = useState(false);
  
  const [formData, setFormData] = useState({
    page_title: '',
    page_slug: '',
    page_content: '',
    meta_description: '',
    page_type: 'static',
    is_published: false,
    banner_image: '',
    display_order: 0
  });

  // Toolbar formatting functions
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
  };

  const insertImage = () => {
    const url = prompt('Masukkan URL gambar:');
    if (url) {
      execCommand('insertImage', url);
    }
  };

  const insertLink = () => {
    const url = prompt('Masukkan URL link:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const insertTable = () => {
    const rows = prompt('Jumlah baris:', '3');
    const cols = prompt('Jumlah kolom:', '3');
    if (rows && cols) {
      let table = '<table border="1" style="border-collapse: collapse; width: 100%;">';
      for (let i = 0; i < parseInt(rows); i++) {
        table += '<tr>';
        for (let j = 0; j < parseInt(cols); j++) {
          table += '<td style="padding: 8px; border: 1px solid #ddd;">Cell</td>';
        }
        table += '</tr>';
      }
      table += '</table>';
      execCommand('insertHTML', table);
    }
  };

  const changeTextColor = () => {
    const color = prompt('Masukkan kode warna (misal: #ff0000):', '#000000');
    if (color) {
      execCommand('foreColor', color);
    }
  };

  const changeBackgroundColor = () => {
    const color = prompt('Masukkan kode warna background (misal: #ffff00):', '#ffffff');
    if (color) {
      execCommand('backColor', color);
    }
  };

  const insertHTML = () => {
    const html = prompt('Masukkan HTML:');
    if (html) {
      execCommand('insertHTML', html);
    }
  };

  const fetchHalaman = useCallback(async () => {
    try {
      setFetchingData(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`http://localhost:5000/api/pmb/halaman/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // API response structure: { success: true, data: page }
      const pageData = res.data.data || res.data;
      
      setFormData({
        page_title: pageData.page_title || '',
        page_slug: pageData.page_slug || '',
        page_content: pageData.page_content || '',
        meta_description: pageData.meta_description || '',
        page_type: pageData.page_type || 'static',
        is_published: pageData.is_published || false,
        banner_image: pageData.banner_image || '',
        display_order: pageData.display_order || 0
      });
      
      toast.success('Data halaman berhasil dimuat');
    } catch (error) {
      console.error('Error fetching halaman:', error);
      toast.error('Gagal memuat data halaman: ' + (error.response?.data?.message || error.message));
    } finally {
      setFetchingData(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchHalaman();
    }
  }, [id, fetchHalaman]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleContentChange = (e) => {
    setFormData({
      ...formData,
      page_content: e.target.innerHTML
    });
  };

  const generateSlug = () => {
    const slug = formData.page_title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFormData({ ...formData, page_slug: slug });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (id) {
        await axios.put(`http://localhost:5000/api/pmb/halaman/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Halaman berhasil diupdate!');
      } else {
        await axios.post('http://localhost:5000/api/pmb/halaman', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Halaman berhasil dibuat!');
      }
      navigate('/pmb/halaman');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal menyimpan halaman');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="halaman-editor-container">
      {fetchingData && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Memuat data halaman...</p>
          </div>
        </div>
      )}
      
      <div className="editor-header">
        <div className="header-left">
          <button 
            className="btn-back"
            onClick={() => navigate('/pmb/halaman')}
          >
            ← Kembali
          </button>
          <h1>{id ? 'Edit Halaman' : 'Buat Halaman Baru'}</h1>
        </div>
        <div className="header-right">
          <button 
            className={`btn-toggle ${previewMode ? 'active' : ''}`}
            onClick={() => setPreviewMode(!previewMode)}
          >
            {previewMode ? '📝 Editor' : '👁️ Preview'}
          </button>
          <button 
            className={`btn-toggle ${showCode ? 'active' : ''}`}
            onClick={() => setShowCode(!showCode)}
          >
            {'</>'} Code
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="editor-form">
        <div className="editor-layout">
          {/* Left Panel - Form Fields */}
          <div className="editor-sidebar">
            <div className="form-section">
              <h3>Informasi Halaman</h3>
              
              <div className="form-group">
                <label>Judul Halaman *</label>
                <input
                  type="text"
                  name="page_title"
                  className="form-control"
                  value={formData.page_title}
                  onChange={handleChange}
                  onBlur={generateSlug}
                  required
                  placeholder="Masukkan judul halaman"
                />
              </div>

              <div className="form-group">
                <label>Slug URL *</label>
                <div className="input-group">
                  <input
                    type="text"
                    name="page_slug"
                    className="form-control"
                    value={formData.page_slug}
                    onChange={handleChange}
                    required
                    placeholder="url-halaman"
                  />
                  <button 
                    type="button" 
                    className="btn-generate"
                    onClick={generateSlug}
                    title="Generate dari judul"
                  >
                    ⚡
                  </button>
                </div>
                <small>URL: /pmb/{formData.page_slug || 'url-halaman'}</small>
              </div>

              <div className="form-group">
                <label>Deskripsi Singkat</label>
                <textarea
                  name="meta_description"
                  className="form-control"
                  value={formData.meta_description}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Deskripsi untuk SEO dan preview (max 160 karakter)"
                  maxLength="160"
                />
                <small>{formData.meta_description?.length || 0}/160 karakter</small>
              </div>

              <div className="form-group">
                <label>Tipe Halaman</label>
                <select
                  name="page_type"
                  className="form-control"
                  value={formData.page_type}
                  onChange={handleChange}
                >
                  <option value="static">Halaman Statis</option>
                  <option value="informasi">Informasi</option>
                  <option value="pengumuman">Pengumuman</option>
                  <option value="berita">Berita</option>
                  <option value="prosedur">Prosedur Pendaftaran</option>
                </select>
              </div>

              <div className="form-group">
                <label>Banner Image URL</label>
                <input
                  type="text"
                  name="banner_image"
                  className="form-control"
                  value={formData.banner_image}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className="form-group">
                <label>Status Publikasi</label>
                <select
                  name="is_published"
                  className="form-control"
                  value={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.value === 'true' })}
                >
                  <option value="false">Draft</option>
                  <option value="true">Published</option>
                </select>
              </div>

              <div className="form-group">
                <label>Urutan Tampil</label>
                <input
                  type="number"
                  name="display_order"
                  className="form-control"
                  value={formData.display_order}
                  onChange={handleChange}
                  min="0"
                />
                <small>Urutan tampil di menu navigasi</small>
              </div>

              <div className="form-actions-sidebar">
                <button 
                  type="submit" 
                  className="btn btn-primary btn-block"
                  disabled={loading}
                >
                  {loading ? 'Menyimpan...' : (id ? 'Update Halaman' : 'Simpan Halaman')}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary btn-block"
                  onClick={() => navigate('/pmb/halaman')}
                >
                  Batal
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Content Editor */}
          <div className="editor-main">
            {!previewMode ? (
              <>
                {/* Toolbar */}
                <div className="editor-toolbar">
                  <div className="toolbar-group">
                    <button type="button" onClick={() => execCommand('undo')} title="Undo">
                      ↶
                    </button>
                    <button type="button" onClick={() => execCommand('redo')} title="Redo">
                      ↷
                    </button>
                  </div>

                  <div className="toolbar-divider"></div>

                  <div className="toolbar-group">
                    <select onChange={(e) => execCommand('formatBlock', e.target.value)} defaultValue="">
                      <option value="" disabled>Style</option>
                      <option value="p">Paragraph</option>
                      <option value="h1">Heading 1</option>
                      <option value="h2">Heading 2</option>
                      <option value="h3">Heading 3</option>
                      <option value="h4">Heading 4</option>
                      <option value="h5">Heading 5</option>
                      <option value="h6">Heading 6</option>
                      <option value="pre">Code Block</option>
                    </select>
                  </div>

                  <div className="toolbar-divider"></div>

                  <div className="toolbar-group">
                    <button type="button" onClick={() => execCommand('bold')} title="Bold">
                      <strong>B</strong>
                    </button>
                    <button type="button" onClick={() => execCommand('italic')} title="Italic">
                      <em>I</em>
                    </button>
                    <button type="button" onClick={() => execCommand('underline')} title="Underline">
                      <u>U</u>
                    </button>
                    <button type="button" onClick={() => execCommand('strikeThrough')} title="Strike">
                      <s>S</s>
                    </button>
                  </div>

                  <div className="toolbar-divider"></div>

                  <div className="toolbar-group">
                    <button type="button" onClick={changeTextColor} title="Text Color">
                      🎨
                    </button>
                    <button type="button" onClick={changeBackgroundColor} title="Highlight">
                      🖍️
                    </button>
                  </div>

                  <div className="toolbar-divider"></div>

                  <div className="toolbar-group">
                    <button type="button" onClick={() => execCommand('justifyLeft')} title="Align Left">
                      ≡
                    </button>
                    <button type="button" onClick={() => execCommand('justifyCenter')} title="Center">
                      ≣
                    </button>
                    <button type="button" onClick={() => execCommand('justifyRight')} title="Align Right">
                      ≡
                    </button>
                    <button type="button" onClick={() => execCommand('justifyFull')} title="Justify">
                      ≣
                    </button>
                  </div>

                  <div className="toolbar-divider"></div>

                  <div className="toolbar-group">
                    <button type="button" onClick={() => execCommand('insertUnorderedList')} title="Bullet List">
                      • List
                    </button>
                    <button type="button" onClick={() => execCommand('insertOrderedList')} title="Numbered List">
                      1. List
                    </button>
                    <button type="button" onClick={() => execCommand('indent')} title="Indent">
                      →
                    </button>
                    <button type="button" onClick={() => execCommand('outdent')} title="Outdent">
                      ←
                    </button>
                  </div>

                  <div className="toolbar-divider"></div>

                  <div className="toolbar-group">
                    <button type="button" onClick={insertLink} title="Insert Link">
                      🔗
                    </button>
                    <button type="button" onClick={() => execCommand('unlink')} title="Remove Link">
                      🔗✗
                    </button>
                    <button type="button" onClick={insertImage} title="Insert Image">
                      🖼️
                    </button>
                    <button type="button" onClick={insertTable} title="Insert Table">
                      ⊞
                    </button>
                    <button type="button" onClick={insertHTML} title="Insert HTML">
                      {'</>'}
                    </button>
                  </div>

                  <div className="toolbar-divider"></div>

                  <div className="toolbar-group">
                    <button type="button" onClick={() => execCommand('removeFormat')} title="Clear Format">
                      ✗ Format
                    </button>
                  </div>
                </div>

                {/* Editor Area */}
                {!showCode ? (
                  <div 
                    className="editor-content"
                    contentEditable
                    dangerouslySetInnerHTML={{ __html: formData.page_content }}
                    onInput={handleContentChange}
                    onBlur={handleContentChange}
                    placeholder="Mulai menulis konten halaman di sini..."
                  />
                ) : (
                  <textarea
                    className="editor-code"
                    value={formData.page_content}
                    onChange={(e) => setFormData({ ...formData, page_content: e.target.value })}
                    placeholder="Edit HTML source code..."
                  />
                )}
              </>
            ) : (
              /* Preview Mode */
              <div className="editor-preview">
                <div className="preview-header">
                  <h2>{formData.page_title || 'Judul Halaman'}</h2>
                  {formData.meta_description && (
                    <p className="preview-description">{formData.meta_description}</p>
                  )}
                </div>
                <div 
                  className="preview-content"
                  dangerouslySetInnerHTML={{ __html: formData.page_content }}
                />
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default HalamanEditor;
